import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAbility } from '@casl/react';
import { AlertTriangle, Download, Flag } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { PageHeader } from '../../components/common/PageHeader';
import AccessRestricted from '../../components/AccessRestricted';
import { CourseThumb } from '../../components/learning/CourseThumb';
import { LoadingPageSkeleton } from '../../components/common/LoadingPageSkeleton';
import { AbilityContext } from '../../context/casl/AbilityContext';
import { useAuth } from '../../context/AuthContext';
import { ACTIONS, RESOURCES } from '../../constants/permissions.constants';
import { learningApi } from '../../utils/learning-api';
import {
    AssignmentRow,
    CourseCertificate,
    LibraryCourse,
    MyLearningResponse,
} from '../../types/learning.types';
import {
    certificateExpiryClass,
    courseAssignmentStatusPillClass,
    courseContentTypeLabel,
    durationLabel,
    formatLearningDate,
    learnerCtaLabel,
    learnerDueLabel,
} from '../../utils/learning';
import { generateLearningCertificatePdf } from '../../utils/learning-certificate-pdf';
import { cn } from '../../components/ui/utils';

type TabValue = 'assigned' | 'self' | 'browse' | 'awards';

const GRID_CLASS = 'grid gap-4 sm:grid-cols-2 xl:grid-cols-3';

const EmptyState: React.FC<{ title: string; body: string; action?: React.ReactNode }> = ({
    title,
    body,
    action,
}) => (
    <div className="rounded-xl border border-dashed border-border p-14 text-center">
        <div className="text-base font-semibold">{title}</div>
        <p className="mt-1.5 text-sm text-muted-foreground">{body}</p>
        {action && <div className="mt-3.5 flex justify-center">{action}</div>}
    </div>
);

interface LearnerCardProps {
    row: AssignmentRow;
    durationMinutes: number | null;
    onOpen: (row: AssignmentRow) => void;
}

/**
 * Learner-facing course card (prototype "My Learning").
 *
 * The header is the shared `CourseThumb`: the course thumbnail when it has one, otherwise the
 * category colour and a book glyph. The category colour is data, so the pills' inline styles
 * are the one exception the design language allows; status colours come from utils/learning.
 */
const LearnerCard: React.FC<LearnerCardProps> = ({ row, durationMinutes, onOpen }) => {
    const colour = row.category?.colour ?? null;

    return (
        <div className="flex flex-col overflow-hidden rounded-xl border border-border bg-card">
            <div className="relative flex h-24 items-center justify-center">
                <CourseThumb thumbnailUrl={row.thumbnailUrl} categoryColour={colour} iconSize={30} />
                {row.category && (
                    <span
                        className="absolute left-2.5 top-2.5 rounded-full border bg-white px-2 py-0.5 text-xs font-semibold"
                        style={{ color: row.category.colour, borderColor: row.category.colour }}
                    >
                        {row.category.name}
                    </span>
                )}
                {durationMinutes != null && (
                    <span className="absolute bottom-2.5 right-2.5 rounded-full bg-white/90 px-2 py-0.5 text-xs text-muted-foreground">
                        {durationLabel(durationMinutes)}
                    </span>
                )}
            </div>

            <div className="flex flex-1 flex-col gap-2 p-3.5">
                <div className="flex flex-wrap items-center gap-1.5">
                    <span
                        className={cn(
                            'rounded-full px-2 py-0.5 text-xs font-medium',
                            courseAssignmentStatusPillClass(row.status, row.overdue)
                        )}
                    >
                        {row.status}
                    </span>
                    {row.isOnboardingCourse && (
                        <span className="inline-flex items-center gap-1 rounded-full border border-blue-200 bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-600">
                            <Flag size={12} /> Onboarding
                        </span>
                    )}
                </div>

                <h3 className="text-sm font-semibold leading-snug">{row.courseTitle}</h3>

                <div
                    className={cn(
                        'text-xs',
                        row.overdue ? 'text-destructive' : 'text-muted-foreground'
                    )}
                >
                    {learnerDueLabel(row)}
                </div>

                <Button size="sm" className="mt-auto w-full" onClick={() => onOpen(row)}>
                    {learnerCtaLabel(row)}
                </Button>
            </div>
        </div>
    );
};

interface BrowseCardProps {
    course: LibraryCourse;
    enrolling: boolean;
    onEnroll: (course: LibraryCourse) => void;
}

const BrowseCard: React.FC<BrowseCardProps> = ({ course, enrolling, onEnroll }) => {
    const colour = course.category?.colour ?? null;

    return (
        <div className="flex flex-col overflow-hidden rounded-xl border border-border bg-card">
            <div className="relative flex h-24 items-center justify-center">
                <CourseThumb thumbnailUrl={course.thumbnailUrl} categoryColour={colour} iconSize={30} />
                {course.category && (
                    <span
                        className="absolute left-2.5 top-2.5 rounded-full border bg-white px-2 py-0.5 text-xs font-semibold"
                        style={{ color: course.category.colour, borderColor: course.category.colour }}
                    >
                        {course.category.name}
                    </span>
                )}
            </div>

            <div className="flex flex-1 flex-col gap-2 p-3.5">
                <div className="flex flex-wrap items-center gap-1.5">
                    <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                        {courseContentTypeLabel(course.contentType)}
                    </span>
                    {course.issuesCertificate && (
                        <span className="rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700">
                            Certificate
                        </span>
                    )}
                </div>

                <h3 className="text-sm font-semibold leading-snug">{course.title}</h3>

                {course.description && (
                    <p className="line-clamp-2 text-xs leading-relaxed text-muted-foreground">
                        {course.description}
                    </p>
                )}

                {course.enrolled ? (
                    <Button variant="outline" size="sm" className="mt-auto w-full" disabled>
                        In My Courses
                    </Button>
                ) : (
                    <Button
                        size="sm"
                        className="mt-auto w-full"
                        onClick={() => onEnroll(course)}
                        disabled={enrolling}
                    >
                        Add to My Courses
                    </Button>
                )}
            </div>
        </div>
    );
};

export default function MyLearningPage() {
    const ability = useAbility(AbilityContext);
    const navigate = useNavigate();
    const { currentUser } = useAuth();

    const canRead = ability.can(ACTIONS.READ, RESOURCES.COURSE_ENROLLMENT);

    const [tab, setTab] = useState<TabValue>('assigned');
    const [data, setData] = useState<MyLearningResponse | null>(null);
    const [library, setLibrary] = useState<LibraryCourse[]>([]);
    const [certificates, setCertificates] = useState<CourseCertificate[]>([]);
    const [loading, setLoading] = useState(true);
    const [enrollingId, setEnrollingId] = useState<number | null>(null);

    const load = useCallback(async () => {
        try {
            setLoading(true);
            const [my, libraryCourses, certs] = await Promise.all([
                learningApi.learner.my(),
                learningApi.learner.library(),
                learningApi.learner.certificates(),
            ]);
            setData(my);
            setLibrary(libraryCourses);
            setCertificates(certs);
        } catch (err) {
            toast.error(err instanceof Error ? err.message : 'Failed to load your learning');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (canRead) load();
    }, [canRead, load]);

    // The library carries the duration the assignment rows don't.
    const durationByCourseId = useMemo(() => {
        const map = new Map<number, number | null>();
        library.forEach((course) => map.set(course.id, course.estimatedDurationMinutes));
        return map;
    }, [library]);

    const handleOpen = (row: AssignmentRow) => {
        // Not yet opened → the intro; started or finished → straight into the player.
        navigate(
            row.status === 'Assigned'
                ? `/my-learning/${row.courseId}`
                : `/my-learning/${row.courseId}/player`
        );
    };

    const handleEnroll = async (course: LibraryCourse) => {
        try {
            setEnrollingId(course.id);
            await learningApi.learner.enroll(course.id);
            toast.success(`Added "${course.title}" to My Courses`);
            await load();
            setTab('self');
        } catch (err) {
            toast.error(err instanceof Error ? err.message : 'Failed to add the course');
        } finally {
            setEnrollingId(null);
        }
    };

    if (!canRead) return <AccessRestricted />;
    if (loading) return <LoadingPageSkeleton />;

    const stats = data?.stats;
    const assigned = data?.assigned ?? [];
    const self = data?.self ?? [];

    const triggerClass = 'rounded-full px-4 py-2 whitespace-nowrap data-[state=active]:bg-white';

    return (
        <div className="space-y-4 p-2 md:space-y-6 md:p-0">
            <PageHeader
                title="My Learning"
                description="Everything you've been assigned, everything you've started, and the library you can explore."
            />

            {stats && stats.overdue > 0 && (
                <div className="flex items-center gap-2 rounded-lg border border-destructive/30 bg-destructive/5 px-4 py-3">
                    <AlertTriangle size={16} className="text-destructive" />
                    <span className="text-sm text-destructive">
                        {stats.overdue} assigned course(s) are past their due date.
                    </span>
                </div>
            )}

            <Tabs value={tab} onValueChange={(value) => setTab(value as TabValue)} className="w-full">
                <TabsList className="no-scrollbar flex h-auto w-full flex-nowrap overflow-x-auto rounded-full bg-gray-100 p-1 md:w-fit">
                    <TabsTrigger value="assigned" className={triggerClass}>
                        Assigned to Me ({stats?.assigned ?? 0})
                    </TabsTrigger>
                    <TabsTrigger value="self" className={triggerClass}>
                        My Courses ({self.length})
                    </TabsTrigger>
                    <TabsTrigger value="browse" className={triggerClass}>
                        Browse Library ({library.length})
                    </TabsTrigger>
                    <TabsTrigger value="awards" className={triggerClass}>
                        Awards &amp; Certificates ({certificates.length})
                    </TabsTrigger>
                </TabsList>

                <TabsContent value="assigned" className="pt-4">
                    {assigned.length === 0 ? (
                        <EmptyState
                            title="Nothing assigned to you right now"
                            body="When HR assigns a course it lands here with its due date."
                        />
                    ) : (
                        <div className={GRID_CLASS}>
                            {assigned.map((row) => (
                                <LearnerCard
                                    key={row.id}
                                    row={row}
                                    durationMinutes={durationByCourseId.get(row.courseId) ?? null}
                                    onOpen={handleOpen}
                                />
                            ))}
                        </div>
                    )}
                </TabsContent>

                <TabsContent value="self" className="pt-4">
                    {self.length === 0 ? (
                        <EmptyState
                            title="You haven't started any courses yet"
                            body="Explore the library to find one."
                            action={
                                <Button size="sm" onClick={() => setTab('browse')}>
                                    Browse Library
                                </Button>
                            }
                        />
                    ) : (
                        <div className={GRID_CLASS}>
                            {self.map((row) => (
                                <LearnerCard
                                    key={row.id}
                                    row={row}
                                    durationMinutes={durationByCourseId.get(row.courseId) ?? null}
                                    onOpen={handleOpen}
                                />
                            ))}
                        </div>
                    )}
                </TabsContent>

                <TabsContent value="browse" className="pt-4">
                    {library.length === 0 ? (
                        <EmptyState
                            title="No courses are available to you yet"
                            body="Courses appear here once HR makes them visible to your department or job role."
                        />
                    ) : (
                        <div className={GRID_CLASS}>
                            {library.map((course) => (
                                <BrowseCard
                                    key={course.id}
                                    course={course}
                                    enrolling={enrollingId === course.id}
                                    onEnroll={handleEnroll}
                                />
                            ))}
                        </div>
                    )}
                </TabsContent>

                <TabsContent value="awards" className="pt-4">
                    {certificates.length === 0 ? (
                        <EmptyState
                            title="No certificates yet"
                            body="They appear as soon as you complete a certificate-issuing course."
                        />
                    ) : (
                        <div className={GRID_CLASS}>
                            {certificates.map((certificate) => (
                                <Card key={certificate.id} className="gap-1.5 p-4">
                                    <div className="text-sm font-semibold">{certificate.courseTitle}</div>
                                    <div className="font-mono text-xs text-muted-foreground">
                                        {certificate.certificateNumber}
                                    </div>
                                    <div className="text-xs text-muted-foreground">
                                        Issued {formatLearningDate(certificate.issuedDate)}
                                    </div>
                                    <div className={cn('text-xs', certificateExpiryClass(certificate.expiryDate))}>
                                        Expires:{' '}
                                        {certificate.expiryDate
                                            ? formatLearningDate(certificate.expiryDate)
                                            : 'No expiry'}
                                    </div>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        className="mt-1.5 w-full"
                                        onClick={() =>
                                            void generateLearningCertificatePdf(
                                                certificate,
                                                currentUser?.name || ''
                                            )
                                        }
                                    >
                                        <Download size={14} /> Download PDF
                                    </Button>
                                </Card>
                            ))}
                        </div>
                    )}
                </TabsContent>
            </Tabs>
        </div>
    );
}
